/* eslint-disable no-useless-catch */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* 
  Copyright (c) 2024, Ouster, Inc.
  Separate the category from driveList store following single responsibility principle
  Prepare for the future when studio allows user to manage their category, such as
  rename, edit, delete
*/

import { mapCategoryForDisplay } from '@ouster/webviz-react-components';

import * as Sentry from '@sentry/react';
import {
  action,
  Action as EzPzAction,
  Thunk as EzPzThunk,
  thunk,
} from 'easy-peasy';
import { orderBy } from 'lodash';

import { DataCategory, LoadStatus } from '../types';
import { BASE_ORG_API } from '../utils/constants';
import { fetchWithAuthV2 } from '../utils/requests';

export type State = {
  dataCategories?: DataCategory[];
  dataCategoriesLoadStatus: LoadStatus;
};

export const defaultState: State = {
  dataCategoriesLoadStatus: 'notStarted',
};

export interface DataCategoryStateModel extends State {
  loadDataCategories: EzPzThunk<DataCategoryStateModel, number>;
  setDataCategoriesLoadStatus: EzPzAction<DataCategoryStateModel, LoadStatus>;
  setDataCategories: EzPzAction<DataCategoryStateModel, DataCategory[]>;
  addDataCategory: EzPzThunk<
    DataCategoryStateModel,
    { organization_id: number; name: string }
  >;
}

export const model: DataCategoryStateModel = {
  ...defaultState,
  loadDataCategories: thunk(async (actions, orgId) => {
    const path = `${BASE_ORG_API}/${orgId}/data_categories/`;
    try {
      /* 
        we have a generic category for customer to upload data, whose organization_id is null
        {
          "description": "A universal category for all organizations.",
          "id": 108,
          "name": "Default-Customer-Category",
          "organization_id": null
        },
      */
      const dataCategories = (await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to load data categories',
      })) as { id: number; name: string; organization_id: number | null }[];

      /* 
        All non ouster user will upload their recording to a standard default category.
        it is a universal category whose organization_id is null.
        Ouster user will not see this category in their dropdown.
        Non ouster user will not see the category dropdown at all. But all their recording will auto submit to that category.
      */
      const filteredDataCategories = dataCategories
        .filter(
          (category) =>
            (orgId === 1 && category.organization_id) ||
            (orgId !== 1 && !category.organization_id)
        )
        .map((category) => ({
          id: category.id,
          name: category.name,
        })) as DataCategory[];

      /* 
        As requirement by Angus, we need to map category in DB to a short list
        The first matching category in original table will be the mapped category
        and the recording will be uploaded under that category
      */
      const mappedDataCategories: DataCategory[] = [];
      filteredDataCategories.forEach((category) => {
        if (
          !mappedDataCategories.some(
            (cate) => cate.name === mapCategoryForDisplay(category.name)
          )
        ) {
          mappedDataCategories.push({
            ...category,
            name: mapCategoryForDisplay(category.name),
          });
        }
      });
      actions.setDataCategories(mappedDataCategories);
    } catch (err) {
      console.error('Failed to load data categories: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'DataCategoryStateModel',
          thunk: 'loadDataCategories',
        },
        extra: { organization_id: orgId },
      });
    }
  }),
  setDataCategoriesLoadStatus: action((state, status) => {
    state.dataCategoriesLoadStatus = status;
  }),
  setDataCategories: action((state, newCategories) => {
    state.dataCategories = orderBy(newCategories, 'name');
  }),
  addDataCategory: thunk(
    async (actions, { organization_id, name }, { getState }) => {
      const path = `${BASE_ORG_API}/${organization_id}/data_categories/`;
      try {
        const resp = (await fetchWithAuthV2(path, {
          method: 'POST',
          fallbackErrorMessage: 'Failed to create data category',
          body: JSON.stringify({ name }),
        })) as DataCategory;
        const orgCategories = getState().dataCategories || [];
        actions.setDataCategories([...orgCategories, resp]);

        return resp;
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            section: 'DataCategoryStateModel',
            thunk: 'addDataCategory',
          },
          extra: { organization_id, name },
        });
        throw err;
      }
    }
  ),
};
